import moment from "moment";
import i18n from "@/i18n";

/**
 * @summary Obtém a hora local no formato do idioma corrente.
 * @returns {String} Uma String da hora local formatado para o idioma corrente.
 */
const GetLocalTime = () =>
{
	const timeFormat = i18n.locale === "en-US" ? "hh:mm A" : "HH:mm";
	const currentTime = moment(new Date());

	return currentTime.format(timeFormat);
};

/**
 * @summary Realiza o parse de um tempo do formato 24h para o do idioma corrente.
 * @param {String} time Tempo a ser convertido
 * @returns {String} Uma String da hora local formatado para o idioma corrente.
 */
const ParseTime = (time) =>
{
	const timeFormat = i18n.locale === "en-US" ? "hh:mm A" : "HH:mm";
	const currentTime = moment(time, [ "hh:mm A", "HH:mm" ]);

	return currentTime.format(timeFormat);
};

/**
 * @summary Realiza o parse de um tempo do formato do idioma vigente para o formato 24h.
 * @param {String} time Tempo a ser convertido
 * @returns {String} Uma String da hora local formatado em 24h.
 */
const ParseTimeTo24hFormat = (time) =>
{
	const timeFormat = "HH:mm";
	const currentTime = moment(time, [ "hh:mm A", "HH:mm" ]);

	return currentTime.format(timeFormat);
};

/**
 * @summary Obtém a data local no formato do idioma corrente.
 * @returns {String} Uma String da data local formatado para o idioma corrente.
 */
const GetLocalDate = () =>
{
	const dateFormat = i18n.locale === "en-US" ? "MM/DD/YYYY" : "DD/MM/YYYY";
	const currentDateTime = moment(new Date());

	return currentDateTime.format(dateFormat);
};

/**
 * @summary Retorna uma data com n dia/mês/ano(from) da data atual e local no idioma corrente.
 * Caso não haja parâmetros, retorna data atual.
 * @param {int} n Quantidade de dias, mês ou ano a ser obtida a data.
 * @param {String} from Parâmetro de período (days/months/years).
 * @returns {String} Uma String da data local formatado para o idioma corrente.
 */
const GetPreviousLocalDate = (n, from) =>
{
	const dateFormat = i18n.locale === "en-US" ? "MM/DD/YYYY" : "DD/MM/YYYY";
	const currentDateTime = moment(new Date());

	switch (from)
	{
	case "days": return currentDateTime.subtract(n, "day").format(dateFormat);
	case "months": return currentDateTime.subtract(n, "month").format(dateFormat);
	case "years": return currentDateTime.subtract(n, "year").format(dateFormat);
	default: return currentDateTime.format(dateFormat);
	}
};

/**
 * @summary Retorna uma data com n dia/mês/ano(from) da data atual e local no formato da ISO 8601.
 * Caso não haja parâmetros, retorna data atual.
 * @param {int} n Quantidade de dias, mês ou ano a ser obtida a data.
 * @param {String} from Parâmetro de período (days/months/years)
 * @returns {String} Uma String formatada pra o padrão do idioma.
 */
const GetCurrentOrPreviousLocalDateISOFormat = (n = "", from = "") =>
{
	const dateFormat = "YYYY-MM-DD";

	const currentDateTime = moment(new Date());
	if (n === "" || from === "")
	{
		return currentDateTime.format(dateFormat);
	}

	switch (from)
	{
	case "days": return currentDateTime.subtract(n, "day").format(dateFormat);
	case "months": return currentDateTime.subtract(n, "month").format(dateFormat);
	case "years": return currentDateTime.subtract(n, "year").format(dateFormat);
	default: return currentDateTime.format(dateFormat);
	}
};

/**
 * @summary Retorna uma data/hora com n dia/mês/ano(from) da data atual e local.
 * Caso não haja parâmetros, retorna data/hora atual.
 * @param {int} n Quantidade de dias, mês ou ano a ser obtida a data.
 * @param {String} from Parâmetro de período (days/months/years)
 * @returns {Object} Um objeto do moment.
 */
const GetLocalDateTime = (n = "", from = "") =>
{
	const currentDateTime = moment(new Date());
	const dateTimeFormat = i18n.locale === "en-US" ? "MM/DD/YYYY hh:mm A" : "DD/MM/YYYY HH:mm";

	if (n === "" || from === "")
	{
		return moment(currentDateTime).format(dateTimeFormat);
	}

	switch (from)
	{
	case "days": return currentDateTime.subtract(n, "day").format(dateTimeFormat);
	case "months": return currentDateTime.subtract(n, "month").format(dateTimeFormat);
	case "years": return currentDateTime.subtract(n, "year").format(dateTimeFormat);
	default: return currentDateTime.format(dateTimeFormat);
	}
};

/**
 * @summary Realiza o parse de um formato de dateTime para o formato padrão do idioma corrente.
 * @param {string} dateTime Valor do dateTime para ser convertido.
 * @param {string} originalDateTimeFormat Formato do dateTime.
 * @param {Boolean} includeSeconds Flag para incluir segundos na conversão.
 * @returns {String} Uma String formatada para o padrao do idioma.
 */
const Parse = (dateTime, originalDateTimeFormat, includeSeconds = false) =>
{
	const momentObject = moment(dateTime, originalDateTimeFormat);
	const dateTimeFormat = i18n.locale === "en-US" ? "MM/DD/YYYY hh:mm A" : "DD/MM/YYYY HH:mm";
	const dateTimeWithSecondsFormat = i18n.locale === "en-US" ? "MM/DD/YYYY hh:mm:ss A" : "DD/MM/YYYY HH:mm:ss";

	if (includeSeconds) return momentObject.format(dateTimeWithSecondsFormat);
	return momentObject.format(dateTimeFormat);
};

/**
 * @summary Realiza o parse de uma data em um formato para o do idioma corrente.
 * @param {string} date Data para ser convertido.
 * @param {String} originalDateTimeFormat Formato original da data.
 * @returns {String} Uma String com a data no formato do idioma corrente.
 */
const ParseDate = (date, originalDateFormat) =>
{
	const momentObject = moment(date, originalDateFormat);
	const dateFormat = i18n.locale === "en-US" ? "MM/DD/YYYY" : "DD/MM/YYYY";
	return momentObject.format(dateFormat);
};

/**
 * @summary Transforma Data/Hora do formato do idioma corrente local para formato ISO 8601 (ex: 2000-01-01T00:00).
 * @param {String} localDateTime - Data/Hora em formato local, conforme o idioma corrente.
 * @param {boolean} keepLocalTime - Se `false` (padrão), converte o fuso horário para o horário equivalente em UTC.
 * Se `true`, ignora a conversão, assumindo que a data já está em UTC ou que a mesma é desnecessária.
 * @returns {String} Uma String da data convertida para o formato ISO 8601 (sem o sufixo "Z").
 */
const ToUTCDateTime = (localDateTime, keepLocalTime = false) =>
{
	const dateTimeWithSecondsFormat = i18n.locale === "en-US" ? "MM/DD/YYYY hh:mm:ss A" : "DD/MM/YYYY HH:mm:ss";
	const utcDateTime = moment(localDateTime, dateTimeWithSecondsFormat)
		.utc(keepLocalTime)
		.format()
		.replace("Z", "");
	return utcDateTime;
};

/**
 * @summary Transforma Data/Hora de UTC (YYYY-MM-DDTHH:mm:ss.sss) para Data/Hora local no formato do idioma corrente.
 * @param {String} utcDateTime - Data/Hora em UTC (YYYY-MM-DDTHH:mm:ss).
 * @returns {String} Uma String com a Data/Hora local no formato do idioma corrente.
 */
const ToLocalDateTime = (utcDateTime) =>
{
	const dateTimeFormat = i18n.locale === "en-US" ? "MM/DD/YYYY hh:mm:ss A" : "DD/MM/YYYY HH:mm:ss";
	const dateTimeInUTC = moment.utc(utcDateTime, [ "YYYY-MM-DDTHH:mm:ss.SSS", "YYYY-MM-DDTHH:mm:ss" ]);
	const localDateTime = dateTimeInUTC.local();

	return localDateTime.format(dateTimeFormat);
};

/**
 * @summary Transforma Data/Hora do formato do idioma corrente local para Data/Hora em UTC sem offset.
 * @param {string} localDateTime - Data/Hora em UTC.
 * @returns {string} Uma String no formato ISO 8601 em UTC sem offset.
 */
const ToUTCDateTimeWithoutOffset  = (localDateTime) =>
{
	const dateTimeWithSecondsFormat = i18n.locale === "en-US" ? "MM/DD/YYYY hh:mm:ss A" : "DD/MM/YYYY HH:mm:ss";
	// Converte a data local para UTC e formata
	const utcDateTime = moment(localDateTime, dateTimeWithSecondsFormat)
		.utc(true)
		.format("YYYY-MM-DDTHH:mm:ss");
	return utcDateTime;
};

const ToLocalDate = (utcDateTime) =>
{
	const dateTimeFormat = i18n.locale === "en-US" ? "MM/DD/YYYY" : "DD/MM/YYYY";
	const dateTimeInUTC = moment.utc(utcDateTime, [ "YYYY-MM-DDTHH:mm:ss.SSS", "YYYY-MM-DDTHH:mm:ss" ]);
	const localDateTime = dateTimeInUTC.local();

	return localDateTime.format(dateTimeFormat);
};

/**
 * @summary Transforma Data/Hora de UTC (YYYY-MM-DDTHH:mm:ss.sss) para Data/Hora local no formato aceito para o gráfico.
 * @param {String} utcDateTime - Data/Hora em UTC (YYYY-MM-DDTHH:mm:ss).
 * @returns {String} Uma String com a Data/Hora local no formato aceito para o exio x do plotly.
 */
const ToLocalDateTimeForGraph = (utcDateTime) =>
{
	const dateTimeInUTC = moment.utc(utcDateTime, [ "YYYY-MM-DDTHH:mm:ss.SSS", "YYYY-MM-DDTHH:mm:ss" ]);
	const localDateTime = dateTimeInUTC.local();

	return localDateTime.format("YYYY-MM-DDTHH:mm:ss");
};

/**
 * @summary Obtém o timestamp de um dateTime.
 * @param {String} dateTime Data e Hora no formato especificado.
 * @param {String} dateTimeFormat Formato da data e hora para entendimento da conversão
 * @returns {Number} Um timestamp.
 */
const GetTimeStamp = (dateTime) =>
{
	const dateTimeFormat = i18n.locale === "en-US" ? "MM/DD/YYYY HH:mm:ss A" : "DD/MM/YYYY HH:mm:ss";

	return moment(dateTime, dateTimeFormat).format("X");
};

/**
 * @summary Retorna um objeto com parâmetros iniciais de uma consulta de últimos n dias.
 * @param {*} days Número de dias a ser consultado. Ex: 1 - 24 horas, 30 - 1 mês...
 * @returns {Object} Período inicial para request de últimos n dias.
 */
const CreateInitialPeriod = (days) =>
{
	const period = {
		startDate: GetPreviousLocalDate(days, "days"),
		endDate:   GetLocalDate(),
		startTime: GetLocalTime(),
		endTime:   GetLocalTime(),
	};
	return period;
};

/**
 *
 * @param {String} endDateTime DateTime final do período, formatado no padrão do idioma corrente.
 * @param {String} startDateTime DateTime inicial do período, formatado no padrão do idioma corrente.
 * @param {String} regionToCheck Região para verificação (diferença de days, months ou years).
 * @returns {Number} A quantidade da diferença.
 */
const TimeDifferenceBetweenTwoDateTime = (endDateTime, startDateTime, regionToCheck) =>
{
	const dateTimeFormat = i18n.locale === "en-US" ? "MM/DD/YYYY HH:mm:ss" : "DD/MM/YYYY HH:mm:ss";

	const dtStart = moment(startDateTime, dateTimeFormat);
	const dtEnd = moment(endDateTime, dateTimeFormat);

	return dtEnd.diff(dtStart, regionToCheck, true) >= 1;
};

/**
 * @summary Permite a criação de um objeto do tipo Moment para ações externas. Caso os parametros sejam nulos,
 * retorna um objeto moment do horário local.
 * @param {String} dateTime para ser transformado.
 * @param {String} dateTimeFormat Formato do dateTime a ser transformado.
 * @returns {Object} Um objeto do tipo Moment para tratamento externo,
 */
const GetMomentObject = (dateTime = "", dateTimeFormat = "") =>
{
	if (dateTime === "" || dateTimeFormat === "") return moment(new Date());

	// C# trata AM e PM como "tt" e o momment reconhece apenas como "A"
	// Para manter compatibilidade com a lib e o C# é atribuido "A" no lugar de tt.
	if (dateTimeFormat === "MM/dd/yyyy hh:mm tt") dateTimeFormat = "MM/DD/YYYY hh:mm A";

	return moment(dateTime, dateTimeFormat);
};

/**
* @summary Obtém a diferença de tempo entre um timestamp específico e o momento presente, em minutos.
* @param {String} dateString Timestamp a ser analisado.
* @returns {Number} - Diferença de tempo, em minutos.
*/
const TimeDifferenceInMinutesFromNow = (dateString) =>
{
	const dateTimeReference = GetMomentObject(dateString, "YYYY-MM-DDTHH:mm:ss:SSS");
	const utcDateTimeNow = GetMomentObject(ToUTCDateTime(GetLocalDateTime()), "YYYY-MM-DDTHH:mm:ss:SSS");

	return moment.duration(utcDateTimeNow.diff(dateTimeReference)).asMinutes();
};

/**
 * @summary Obtém o offset do timezone do usuário.
 * @returns O offset local no formato HH:mm para o timespan.
 */
const GetLocalTimeOffset = () =>
{
	const realOffset = new Date().getTimezoneOffset();
	const offSet = Math.abs(realOffset);
	return (realOffset < 0 ? "" : "-") +
		("00" + Math.floor(offSet / 60)).slice(-2) + ":" + ("00" + (offSet % 60)).slice(-2);
};

/**
 * @summary Obtém o formato de data/hora para o idioma vigente.
 * @returns O formato de data/hora localizado ao idioma vigente.
 */
const GetDateTimeFormat = () =>
{
	return i18n.locale === "en-US" ? "MM/dd/yyyy hh:mm tt" : "dd/MM/yyyy HH:mm";
};

/**
 * Converte as horas em dias ou anos se necessário.
 * @returns Hora convertida se necessário.
 */
const ConvertHours = (hours) =>
{
	if (!hours) return "";
	else if (hours < 24)
	{
		const description = hours == 1 ? i18n.t("TableHeaders.Hour") : i18n.t("TableHeaders.Hours");
		return `${hours} ${description}`;
	}
	else if (hours < 8760) return ParseHoursToDays(hours);
	else return ParseHoursToYears(hours);
};

/**
 * @summary Converte as horas em dias.
 * @param {Number} hours - Horas.
 * @returns String traduzida para dias, exemplo: 12 Dias.
 */
const ParseHoursToDays = (hours) =>
{
	const days = hours / 24;
	const roundedDays = Math.round(days);
	const description = roundedDays == 1 ? i18n.t("TableHeaders.Day") : i18n.t("TableHeaders.Days");
	return `${roundedDays} ${description}`;
};

/**
 * @summary Converte as horas em anos.
 * @param {Number} hours - Horas.
 * @returns String traduzida para anos, exemplo: 1 Ano.
 */
const ParseHoursToYears = (days) =>
{
	const years = days / 8760;
	const roundedYears = Math.round(years);
	const description = roundedYears == 1 ? i18n.t("TableHeaders.Year") : i18n.t("TableHeaders.Years");
	return `${roundedYears} ${description}`;
};

/**
 * @summary Obtém o Offset a partir de um nome de fuso horário.
 * @param {string} timezoneName - O nome do fuso horário para o qual o offset será obtido.
 * @returns {string} Ooffset no formato "GMT±HH:mm".
 */
const GetLongOffsetFromIntl = function(timezoneName)
{
	const offset = new Intl.DateTimeFormat("en", { timeZone: timezoneName, timeZoneName: "longOffset" })
		.formatToParts()
		.find((part) => part.type === "timeZoneName").value;
	return offset;
};

/**
 * @summary Obtém o valor do offset de um nome de fuso horário.
 * @param {string} timezoneName - O nome do fuso horário para o qual o valor do deslocamento horário será obtido.
 * @returns {string} O valor offset "HH:mm" ou "-HH:mm".
 */
const GetOffsetValueFromString = function(timezoneName)
{
	const longOffset = GetLongOffsetFromIntl(timezoneName);
	let offsetValue = "";
	offsetValue = longOffset.match(/-?\d+/g);
	if (offsetValue)
	{
		let hours = parseInt(offsetValue[0]);
		const minutes = parseInt(offsetValue[1]) || 0;
		const signal = hours >= 0 ? "" : "-";
		hours = Math.abs(hours);
		offsetValue = `${signal}${hours < 10 ? "0" : ""}${hours}:${minutes < 10 ? "0" : ""}${minutes}`;
	}
	else
	{
		offsetValue = "00:00";
	}
	return offsetValue;
};

export default {
	i18n,
	GetMomentObject,
	GetLocalTime,
	GetLocalDate,
	GetPreviousLocalDate,
	GetLocalDateTime,
	GetCurrentOrPreviousLocalDateISOFormat,
	CreateInitialPeriod,
	TimeDifferenceInMinutesFromNow,
	TimeDifferenceBetweenTwoDateTime,
	ToLocalDate,
	ToLocalDateTime,
	ToLocalDateTimeForGraph,
	ToUTCDateTime,
	Parse,
	ParseDate,
	ParseTime,
	ParseTimeTo24hFormat,
	GetTimeStamp,
	GetLocalTimeOffset,
	GetDateTimeFormat,
	ConvertHours,
	ParseHoursToDays,
	ParseHoursToYears,
	GetLongOffsetFromIntl,
	GetOffsetValueFromString,
	ToUTCDateTimeWithoutOffset,
};
